import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { searchApiRef } from '@backstage/plugin-search-react';
import {
  useShadowRootElements,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';

/*
 * The card row sits inside the reader's content column, which mkdocs centres at
 * the 680px measure — the addon portals into `.md-content`, one level up, so it
 * repeats the measure itself rather than inheriting it.
 */
const useStyles = makeStyles(theme => ({
  root: {
    width: 680,
    maxWidth: 680,
    margin: '0 auto',
    paddingBottom: 26,
    textAlign: 'left',
    /*
     * Phone: the reader drops to a single column narrower than the measure, and
     * a fixed 680px there resolves `margin: 0 auto` to a negative right margin
     * — the row painted ~235px past the viewport. The column's own width is the
     * measure at this breakpoint.
     */
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      maxWidth: 'none',
    },
  },
  rule: {
    height: 1,
    marginBottom: 22,
    background: 'var(--hone-portal-hairline-soft)',
  },
  heading: {
    display: 'block',
    marginBottom: 12,
    fontSize: 13.5,
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: 'normal',
    textTransform: 'none',
    color: 'var(--bui-fg-primary)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 14,
    /* Three cards in a 402px column leave no room for a title; they stack. */
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '14px 16px',
    borderRadius: 10,
    background: 'var(--bui-white)',
    boxShadow: 'var(--hone-portal-elevation-card)',
  },
  title: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.4,
    color: 'var(--bui-fg-primary)',
    textAlign: 'left',
  },
  entityName: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    color: 'var(--hone-portal-fg-tertiary)',
    textAlign: 'left',
  },
  skeletonGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 14,
    /* The placeholders track the cards they stand in for. */
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
}));

type Article = {
  title: string;
  location: string;
  name: string;
  type: string;
};

// A human label for the entity's type. spec.type is only meaningful for
// Components (website/service/library/…); the collator defaults it to "other"
// for every other kind, so fall back to the kind itself (Repository, API,
// Resource, …) there. Title-cased for display.
function entityType(doc: Record<string, any>): string {
  const kind = String(doc.kind ?? '').trim();
  const specType = String(doc.componentType ?? '').trim();
  const raw =
    kind.toLowerCase() === 'component' &&
    specType &&
    specType.toLowerCase() !== 'other'
      ? specType
      : kind || specType;
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
}

export const RelatedArticlesAddon = () => {
  const { entityRef } = useTechDocsReaderPage();
  const { pathname } = useLocation();
  const headings = useShadowRootElements<HTMLHeadingElement>([
    'h1',
    'h2',
    'h3',
  ]);
  const searchApi = useApi(searchApiRef);
  const classes = useStyles();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);

  // Build the query from the current page's own headings — its title plus the
  // section headers — so the search reflects what the page is actually about.
  // Fall back to the doc slug before the content has rendered.
  const headingTerm = headings
    .map(h => (h.textContent ?? '').replace(/¶/g, '').trim())
    .filter(Boolean)
    .join(' ');
  const pageSlug = (pathname.split('/documentation/')[1] ?? '')
    .replace(/\/+$/, '')
    .split('/')
    .pop();
  const term = headingTerm || pageSlug?.replace(/[-_]/g, ' ').trim() || '';

  // Exclude only the page you're on — sibling pages in the same docs (e.g.
  // Access Control / Permission / Auth alongside RBAC) are exactly what should
  // surface. Search doc.location follows /documentation/:namespace/:kind/:name/:path.
  const inDocsPath = (pathname.split('/documentation/')[1] ?? '').replace(
    /\/+$/,
    '',
  );
  const currentPage =
    `/documentation/${entityRef.namespace}/${entityRef.kind}/${entityRef.name}/${inDocsPath}`
      .replace(/\/+$/, '')
      .toLowerCase();

  useEffect(() => {
    const controller = new AbortController();

    if (!term) {
      setArticles([]);
      setLoading(false);
      return () => controller.abort();
    }

    setLoading(true);
    searchApi
      .query(
        { term, types: ['techdocs'], pageLimit: 25 },
        { signal: controller.signal },
      )
      .then(result => {
        const seen = new Set<string>();
        const found: Article[] = [];
        for (const r of result.results) {
          const doc = r.document as any;
          // The techdocs collator indexes per heading, so one page returns
          // several results that differ only by the #anchor in `location`.
          // Collapse them to the page and link to the page top.
          const pagePath = String(doc.location ?? '').split('#')[0];
          const pageKey = pagePath.replace(/\/+$/, '').toLowerCase();
          if (!pageKey || pageKey === currentPage || seen.has(pageKey)) continue;
          seen.add(pageKey);
          found.push({
            title: doc.title,
            location: pagePath,
            name: doc.name ?? '',
            type: entityType(doc),
          });
          // Three fills the card row exactly; a fourth starts a lonely second row.
          if (found.length >= 3) break;
        }
        setArticles(found);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') {
          setArticles([]);
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [term, currentPage, searchApi]);

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.rule} />
        <Skeleton variant="text" width={120} />
        <div className={classes.skeletonGrid}>
          <Skeleton variant="rect" height={72} />
          <Skeleton variant="rect" height={72} />
          <Skeleton variant="rect" height={72} />
        </div>
      </div>
    );
  }

  if (articles.length === 0) return null;

  return (
    <div className={classes.root}>
      <div className={classes.rule} />
      <Typography variant="subtitle2" className={classes.heading}>
        Related articles
      </Typography>
      <div className={classes.grid}>
        {articles.map(article => (
          <Link
            key={article.location}
            component={RouterLink}
            to={article.location}
            className={classes.card}
            underline="none"
          >
            <span className={classes.title}>{article.title}</span>
            {(article.name || article.type) && (
              <span className={classes.entityName}>
                {[article.name, article.type].filter(Boolean).join(' · ')}
              </span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
};
